<template>
  <main
    class="groups-page"
    data-testid="groups-page"
  >
    <div
      class="page-header"
      data-check-hash="2aae6c35c94fcfb415dbe95f408b9ce91ee846ed"
    >
      <h1
        id="groups-h1"
        class="h1"
      >
        Gruppen
      </h1>
      <div
        class="actions"
        style="display:flex;align-items:center;gap:8px;"
      >
        <button
          class="btn btn-primary"
          aria-labelledby="groups-h1"
          data-testid="group-create"
          @click="openNew"
        >
          Neu
        </button>
      </div>
    </div>

    <div class="card">
      <!-- Filterleiste -->
      <div class="filters">
        <input
          v-model="nameQ"
          placeholder="Name/Beschreibung filtern"
          autocomplete="off"
          data-testid="group-search"
          @keyup.enter="/* clientseitig */ null"
        >
        <div
          class="field"
          style="min-width:200px;"
        >
          <label for="gr-status">Status</label>
          <select
            id="gr-status"
            v-model="statusFilter"
            @change="load"
          >
            <option value="">
              Alle
            </option>
            <option value="aktiv">
              Aktiv
            </option>
            <option value="inaktiv">
              Inaktiv
            </option>
          </select>
        </div>
        <div
          v-if="isAdmin"
          class="field"
          style="min-width:220px;"
        >
          <label for="gr-owner">Zugehörigkeit</label>
          <select
            id="gr-owner"
            v-model="ownerFilter"
          >
            <option value="">
              Alle
            </option>
            <option
              v-if="hasGeneralGroups"
              value="general"
            >
              allgemein
            </option>
            <option
              v-for="u in ownerFilterOptions"
              :key="u.id"
              :value="String(u.id)"
            >
              {{ u.displayName }}
            </option>
          </select>
        </div>
        <button
          class="btn btn-primary"
          :disabled="!nameQ && !statusFilter && !ownerFilter"
          @click="resetFilters"
        >
          Reset
        </button>
      </div>

      <div data-testid="group-table">
        <!-- Tabellen-Header -->
        <div :class="['table-header', isAdmin ? 'column-6' : 'column-5']">
          <div
            class="sortable"
            role="button"
            tabindex="0"
            @click="setSort('name')"
            @keydown.enter="setSort('name')"
          >
            Name
            <span v-if="sortKey === 'name'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
          </div>
          <div>Beschreibung</div>
          <div>Status</div>
          <div v-if="isAdmin">
            Zugehörigkeit
          </div>
          <div
            class="sortable"
            role="button"
            tabindex="0"
            @click="setSort('member_count')"
            @keydown.enter="setSort('member_count')"
          >
            Schüler
            <span v-if="sortKey === 'member_count'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
          </div>
          <div class="text-right">
            Aktionen
          </div>
        </div>

        <!-- Zeilen -->
        <div
          v-for="g in sortedItems"
          :key="g.id"
          :class="['table-row', isAdmin ? 'column-6' : 'column-5']"
          data-testid="group-row"
        >
          <div data-testid="group-name">
            <strong>{{ g.name }}</strong>
            <span
              v-if="g.is_private"
              class="badge badge-warn"
              title="privat"
              aria-label="privat"
            >privat</span>
          </div>
          <div class="muted">
            {{ g.description || '' }}
          </div>
          <div class="muted">
            {{ g.status ? 'Aktiv' : 'Inaktiv' }}
          </div>
          <div
            v-if="isAdmin"
            class="muted"
          >
            {{ ownerNameFor(g) }}
          </div>
          <div class="muted">
            {{ g.member_count ?? '–' }}
          </div>
          <div class="row-actions">
            <button
              class="icon-btn"
              title="Schüler zuweisen"
              aria-label="Schüler zuweisen"
              @click="openAssignFor(g)"
            >
              <i
                class="pi pi-user"
                aria-hidden="true"
              />
            </button>
            <button
              v-if="g.is_private || isAdmin"
              class="icon-btn"
              title="Bearbeiten"
              aria-label="Bearbeiten"
              data-testid="group-edit"
              @click="edit(g)"
            >
              <i
                class="pi pi-pencil"
                aria-hidden="true"
              />
            </button>
            <button
              v-if="g.is_private || isAdmin"
              class="icon-btn danger"
              title="Löschen"
              aria-label="Löschen"
              data-testid="group-delete"
              @click="confirmDelete(g)"
            >
              <i
                class="pi pi-trash"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>

        <p
          v-if="filteredItems.length === 0"
          class="muted"
        >
          Keine Einträge.
        </p>
      </div>

      <!-- Pagination -->
      <div
        class="mt-12"
        style="display:flex;align-items:center;gap:8px;justify-content:flex-end;"
      >
        <span
          class="muted"
          style="margin-right:auto"
        >Gesamt: {{ total }}</span>
        <label
          class="muted"
          for="gr-limit"
        >pro Seite:</label>
        <select
          id="gr-limit"
          :value="limit"
          @change="changeLimit"
        >
          <option
            v-for="n in [10,20,50,100]"
            :key="n"
            :value="n"
          >
            {{ n }}
          </option>
        </select>
        <button
          class="btn"
          :disabled="page<=1"
          @click="prevPage"
        >
          Zurück
        </button>
        <span class="muted">Seite {{ page }} / {{ pages }}</span>
        <button
          class="btn"
          :disabled="page>=pages"
          @click="nextPage"
        >
          Weiter
        </button>
      </div>
    </div>

    <!-- Dialoge unverändert -->
    <BaseDialog
      :open="openCreate || !!current"
      :disabled="loading"
      data-testid="group-dialog"
      @close="closeDialog"
    >
      <template #title>
        {{ current ? 'Gruppe bearbeiten' : 'Neue Gruppe' }}
      </template>
      <form @submit.prevent="submit">
        <div class="form">
          <div class="field">
            <label for="name">Name</label>
            <input
              id="name"
              v-model="form.name"
              data-testid="group-name-input"
            >
            <p
              v-if="errors.name"
              class="help danger"
              role="alert"
            >
              {{ errors.name }}
            </p>
          </div>
          <div class="field">
            <label for="desc">Beschreibung (optional)</label>
            <input
              id="desc"
              v-model="form.description"
            >
            <p
              v-if="errors.description"
              class="help danger"
              role="alert"
            >
              {{ errors.description }}
            </p>
          </div>
          <div class="field status-row">
            <div class="col">
              <label
                for="status"
                class="status-label"
              ><input
                id="status"
                v-model="form.status"
                type="checkbox"
                class="chk-small"
              > Aktiv</label>
              <small class="hint">&nbsp;</small>
            </div>
            <div
              v-if="!isAdmin"
              class="col"
            >
              <label class="status-label">
                <input
                  v-model="form.is_private"
                  type="checkbox"
                  class="chk-small"
                > Privat
              </label>
              <small class="hint">Private Gruppen sind nur für Sie sichtbar.</small>
            </div>
            <div
              v-else
              class="col"
            >
              <label for="owner">Zugehörigkeit</label>
              <select
                id="owner"
                v-model.number="form.owner_user_id"
              >
                <option :value="null">
                  allgemein
                </option>
                <option
                  v-for="u in owners"
                  :key="u.id"
                  :value="u.id"
                >
                  {{ u.displayName }}
                </option>
              </select>
            </div>
          </div>
        </div>
        <p
          v-if="errorMsg"
          class="notice notice-danger mt-12"
          role="alert"
          data-testid="toast-error"
        >
          {{ errorMsg }}
        </p>
        <div
          class="mt-12"
          style="display:flex;gap:8px;justify-content:flex-end;"
        >
          <button
            type="button"
            class="btn"
            :disabled="loading"
            @click="closeDialog"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="loading"
            data-testid="group-save"
          >
            <span
              v-if="loading"
              class="spinner"
              aria-hidden="true"
            /> Speichern
          </button>
        </div>
      </form>
    </BaseDialog>

    <BaseDialog
      :open="!!toDelete"
      :disabled="loading"
      data-testid="group-delete-dialog"
      @close="toDelete=null"
    >
      <template #title>
        Gruppe löschen?
      </template>
      <p>Diese Aktion kann nicht rückgängig gemacht werden.</p>
      <div
        class="mt-12"
        style="display:flex;gap:8px;justify-content:flex-end;"
      >
        <button
          class="btn"
          :disabled="loading"
          @click="toDelete=null"
        >
          Abbrechen
        </button>
        <button
          class="btn btn-warn"
          :disabled="loading"
          data-testid="group-delete-confirm"
          @click="doDelete"
        >
          <span
            v-if="loading"
            class="spinner"
            aria-hidden="true"
          /> Löschen
        </button>
      </div>
    </BaseDialog>

    <!-- Zuweisungsdialog: Gruppe → Schüler -->
    <GroupAssignDialog
      :open="openAssign"
      :group="selectedGroup"
      @close="() => { openAssign=false; selectedGroup=null; }"
      @applied="onAssignApplied"
    />

    <!-- Top-Navigation: Hilfe-Button zwischen .session-timer und .profile -->
    <teleport to=".top-nav">
      <span
        id="top-nav-help-groups"
        class="top-nav-help"
      >
        <UiInfoButton title="Hilfe: Gruppen">
          <ul>
            <li>Filter: Name/Beschreibung (Text), Status (Aktiv/Inaktiv) und – nur für Admins – Zugehörigkeit.</li>
            <li>Tabelle: Name, Beschreibung, Status, ggf. Zugehörigkeit (Admin) und Anzahl der Schüler.</li>
            <li>Aktionen pro Zeile: Schüler zuweisen, Bearbeiten, Löschen; „privat“-Badge kennzeichnet private Gruppen.</li>
            <li>Pagination: Anzahl pro Seite wählen und über „Zurück/Weiter“ navigieren.</li>
            <li>Dialog „Neu/Bearbeiten“: Name, optionale Beschreibung; Admins wählen Zugehörigkeit (macht Gruppe privat).</li>
          </ul>
        </UiInfoButton>
      </span>
    </teleport>
  </main>
</template>

<script setup>
import {onMounted, onBeforeUnmount, ref, computed} from 'vue';
import api from '@/services/api';
import {useToast} from 'primevue/usetoast';
import BaseDialog from '@/components/BaseDialog.vue';
import GroupAssignDialog from '@/components/GroupAssignDialog.vue';
import {useAuthStore} from '@/stores/auth';
import UiInfoButton from '@/components/UiInfoButton.vue';

const items = ref([]);
const loading = ref(false);
const openCreate = ref(false);
const openAssign = ref(false);
const selectedGroup = ref(null);
const current = ref(null);
const toDelete = ref(null);
const toast = useToast();
const auth = useAuthStore();
// isAdmin als Computed-Ref, damit in JS isAdmin.value verfügbar ist
const isAdmin = computed(() => auth.isAdmin);

// Zugehörigkeit (für Admins Dropdown + Anzeige)
const owners = ref([]); // [{id, name}]
const ownerMap = computed(() => {
  const m = {};
  for (const u of owners.value) m[u.id] = u.displayName;
  return m;
});
const userId = computed(() => auth.user?.id || null);

// Nur Nutzer, die mindestens eine eigene AKTIVE Gruppe in der Tabelle haben
const eligibleOwnerIds = computed(() => {
  const ids = new Set();
  for (const g of items.value) {
    if (g.is_private && g.status && g.owner_user_id) {
      ids.add(Number(g.owner_user_id));
    }
  }
  return ids;
});

// Filter-Optionen: Schnittmenge aus zuweisbaren Nutzern und aktiven Gruppen-Eignern
const ownerFilterOptions = computed(() => {
  const allowed = eligibleOwnerIds.value;
  return owners.value.filter(u => allowed.has(Number(u.id)));
});

// "allgemein" nur anzeigen, wenn es überhaupt globale Gruppen gibt
const hasGeneralGroups = computed(() => {
  return items.value.some(g => !g.is_private);
});

function ownerNameFor(g) {
  if (!g?.is_private) return 'allgemein';

  if (isAdmin.value && g?.owner_user_id && ownerMap.value[g.owner_user_id]) {
    return ownerMap.value[g.owner_user_id];
  }
  if (!isAdmin.value && g?.owner_user_id && userId.value && g.owner_user_id === userId.value) {
    return 'Ich';
  }
  return 'Privat';
}

async function loadOwners() {
  try {
    const {data} = await api.get('/users/assignable', {params: {roles: '2'}});
    owners.value = data?.data || [];
  } catch {
    owners.value = [];
  }
}

// Owners laden, sobald Dialog geöffnet/Datensatz gewählt wird (nur Admin)
// (entfernt, explizites Laden beim Öffnen/Bearbeiten)

// Filter
const nameQ = ref('');
const statusFilter = ref('');
const ownerFilter = ref('');
const filteredItems = computed(() => {
  const q = nameQ.value.trim().toLowerCase();
  let base = items.value;
  if (statusFilter.value) {
    const want = statusFilter.value === 'aktiv';
    base = base.filter(g => Boolean(g.status) === want);
  }
  // Zugehörigkeits-Filter (nur Admin)
  if (isAdmin.value && ownerFilter.value !== '') {
    if (ownerFilter.value === 'general') {
      base = base.filter(g => !g.is_private);
    } else {
      const ownerId = Number(ownerFilter.value);
      base = base.filter(g => g.is_private && Number(g.owner_user_id) === ownerId);
    }
  }
  if (!q) return base;
  return base.filter(g =>
      (g.name && g.name.toLowerCase().includes(q)) ||
      (g.description && String(g.description).toLowerCase().includes(q))
  );
});

// Sortierung
const sortKey = ref('name');
const sortDir = ref('asc');

const sortedItems = computed(() => {
  const arr = [...filteredItems.value];
  arr.sort((a, b) => {
    let va, vb;
    switch (sortKey.value) {
      case 'member_count':
        va = Number(a.member_count ?? 0);
        vb = Number(b.member_count ?? 0);
        break;
      case 'name':
      default:
        va = a.name ?? '';
        vb = b.name ?? '';
        break;
    }
    let result;
    if (typeof va === 'number' && typeof vb === 'number') {
      result = va - vb;
    } else {
      result = String(va).localeCompare(String(vb), 'de', {sensitivity: 'base'});
    }
    return sortDir.value === 'asc' ? result : -result;
  });
  return arr;
});

function setSort(key) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortDir.value = 'asc';
  }
}

// Pagination
const page = ref(1);
const limit = ref(20);
const total = ref(0);
const pages = ref(0);

const form = ref({
  name: '',
  description: '',
  status: true,
  is_private: isAdmin.value ? false : true,
  owner_user_id: null
});
const errors = ref({});
const errorMsg = ref('');

async function load() {
  loading.value = true;
  if (isAdmin.value && owners.value.length === 0) {
    try {
      await loadOwners();
    } catch {
      //ignore
    }
  }
  const params = {withCounts: true, page: page.value, limit: limit.value};
  if (statusFilter.value) {
    params.status = statusFilter.value;
  }
  const {data} = await api.get('/groups', {params});
  items.value = data.items || [];
  total.value = data.total || 0;
  pages.value = data.pages || 0;
  loading.value = false;
}

async function openNew() {
  // Lehrer vorladen, wenn Admin
  if (isAdmin.value && owners.value.length === 0) {
    await loadOwners();
  }
  // Dialog öffnen
  openCreate.value = true;
}

function nextPage() {
  if (page.value < pages.value) {
    page.value++;
    load();
  }
}

function prevPage() {
  if (page.value > 1) {
    page.value--;
    load();
  }
}

function changeLimit(e) {
  limit.value = Number(e.target.value);
  page.value = 1;
  load();
}

async function edit(row) {
  current.value = row;
  form.value = {
    name: row.name,
    description: row.description || '',
    status: !!row.status,
    is_private: !!row.is_private,
    owner_user_id: isAdmin.value ? (row.owner_user_id ?? null) : null
  };
}

function closeDialog() {
  openCreate.value = false;
  current.value = null;
  form.value = {
    name: '',
    description: '',
    status: true,
    is_private: isAdmin.value ? false : true,
    owner_user_id: null
  };
  errors.value = {};
  errorMsg.value = '';
}

async function submit() {
  loading.value = true;
  errors.value = {};
  errorMsg.value = '';
  try {
    // Payload normalisieren
    const payload = {...form.value};
    if (isAdmin.value) {
      // owner_user_id: Zahl oder null
      const sel = payload.owner_user_id;
      const hasOwner = sel !== null && sel !== undefined && sel !== '';
      payload.owner_user_id = hasOwner ? Number(sel) : null;
      payload.is_private = payload.owner_user_id !== null;
    } else {
      // Lehrer: bei privaten Gruppen eigene user_id als owner mitsenden, sonst null
      payload.owner_user_id = payload.is_private ? userId.value : null;
    }

    if (current.value) {
      const {data} = await api.put(`/groups/${current.value.id}`, payload);
      const idx = items.value.findIndex(i => i.id === data.id);
      if (idx >= 0) {
        // vorhandene Felder (z. B. member_count) erhalten
        items.value[idx] = {...items.value[idx], ...data};
      }
    } else {
      await api.post('/groups', payload);
      // nach neuer Gruppe auf erste Seite springen und komplett neu laden
      page.value = 1;
      await load();
    }
    closeDialog();
    // bei Bearbeitung Liste sicher synchronisieren
    if (current.value) {
      await load();
    }
    toast.add({severity: 'success', summary: 'Gespeichert.', life: 3500});
  } catch (e) {
    const res = e.response?.data;
    if (res?.error?.details?.fields) {
      errors.value = res.error.details.fields;
    } else {
      errorMsg.value = res?.error?.message || 'Aktion fehlgeschlagen.';
    }
  } finally {
    loading.value = false;
  }
}

function confirmDelete(row) {
  toDelete.value = row;
}

function openAssignFor(row) {
  if (!row?.status) return;
  selectedGroup.value = row;
  openAssign.value = true;
}

function onAssignApplied() {
  // Liste neu laden; Dialog toastet bereits
  load();
}

function resetFilters() {
  nameQ.value = '';
  statusFilter.value = '';
  ownerFilter.value = '';
  load();
}

async function doDelete() {
  loading.value = true;
  try {
    await api.delete(`/groups/${toDelete.value.id}`);
    items.value = items.value.filter(i => i.id !== toDelete.value.id);
    toDelete.value = null;
    toast.add({severity: 'success', summary: 'Gelöscht.', life: 3500});
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  if (isAdmin.value) {
    await loadOwners();
  }
  await load();
});

// Hilfe-Button in .top-nav vor .profile einsortieren
onMounted(() => {
  const nav = document.querySelector('.top-nav');
  const el = document.getElementById('top-nav-help-groups');
  if (!nav || !el) return;
  const profile = nav.querySelector('.profile');
  if (profile && profile.parentNode === nav) nav.insertBefore(el, profile);
  else nav.appendChild(el);
});

onBeforeUnmount(() => {
  const el = document.getElementById('top-nav-help-groups');
  if (el && el.parentNode) el.parentNode.removeChild(el);
});
</script>

<style scoped>
/* Komponentenspezifisches Spaltenraster */
.table-header.column-6,
.table-row.column-6 {
  grid-template-columns: 1.2fr 1.2fr 120px 160px 120px 100px;
}

.table-header.column-5,
.table-row.column-5 {
  grid-template-columns: 1.2fr 1.2fr 120px 160px 120px;
}

.table-row {
  padding: 5px 0;
  font-size: 14px;
}

/* Sortierbare Header wie in SchoolYears */
.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  text-decoration: underline;
}

/* Badge für Kennzeichnungen (z. B. "Privat") */
.badge {
  display: inline-block;
  font-size: 12px;
  line-height: 16px;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--muted);
  margin-left: 8px;
}

.badge-warn {
  color: var(--warn);
  border-color: rgba(245, 158, 11, .5);
  background: rgba(245, 158, 11, .08);
}

/* Dialog: Status-Zeile */
.status-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-label {
  text-align: left;
}

.chk-small {
  width: 16px;
  height: 16px;
  vertical-align: middle;
  accent-color: var(--primary);
  margin-right: 8px;
  margin-top: 0px;
}
</style>